import React, { useEffect, useState } from 'react';
import { Stack, Tab, Tabs, Row, Button, Col } from 'react-bootstrap';
import { useGetCancelSubscriptionMutation, usePurchaseHistoryQuery } from 'services';
import { dataLimits, S3_URL, USER_TYPES } from 'constants/index';
import PaginationBox from 'components/PaginationBox';
import JobCardSkeleton from 'components/Common/Skeleton/JobCardSkeleton';
import { downloadFile, getCurrencySymbol, getDaysDifference, getFileNameByURL } from 'utils';
import useTranslate from 'hooks/useTranslate';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import './styles.scss';
import useCommonStore, { setModalConfig } from 'store/common';
import useAuthStore from 'store/auth';
import toast from 'react-hot-toast';

const CurrentPlanHistory: React.FC = () => {
	const { translate } = useTranslate();
	const navigate = useNavigate();
	const { user: authUser }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const [activeKey, setActiveKey] = useState<string>('active');
	const [tabData, setTabData] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);

	const paramsQuery = `purchaseHistoryType=${activeKey}&order=DESC&page=${currentPage}&limit=${dataLimits?.paymentHistory}`;
	const {
		data: { docs = [], meta = {} } = {},
		isPending,
		refetch,
	} = usePurchaseHistoryQuery({ type: activeKey, paramsQuery });
	const { mutateAsync: cancelSubscriptionMutation, isPending: pendingCancelSubscription } =
		useGetCancelSubscriptionMutation();

	useEffect(() => {
		if (activeKey) {
			setCurrentPage(1);
		}
	}, [activeKey]);

	useEffect(() => {
		refetch();
	}, [currentPage, activeKey, refetch]);

	useEffect(() => {
		if (!isPending) {
			setTabData(docs || []);
		}
	}, [docs, isPending]);

	const onCancelSubscription = (purchase_id: string) => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: () => onConfirmClick(purchase_id),
			data: {
				title: translate('Modal.wantToCancelPlan'),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};
	const onConfirmClick = (purchase_id: string) => {
		cancelSubscriptionMutation({ purchase_id }).then((res: any) => {
			if (res.success) {
				toast.success(res?.message);
				hideCommonModal();
				refetch();
			}
		});
	};

	const downloadPDF = async (invoice_file: any = '') => {
		if (invoice_file) {
			downloadFile(S3_URL + invoice_file, getFileNameByURL(invoice_file));
		}
	};

	return (
		<div className="white-box bg-white">
			<Stack className="gap-3 gap-md-4">
				<div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
					<div className="list-heading">
						<h4 className="fw-bold">{translate('Common.currentPlanHistory')}</h4>
					</div>
				</div>
				<div className="connections-tab">
					<Tabs defaultActiveKey={activeKey} onSelect={(k) => setActiveKey(k!)}>
						<Tab eventKey="active" title={translate('Common.active')}>
							{activeKey === 'active' && isPending ? (
								<JobCardSkeleton />
							) : (
								<Row>
									{tabData?.length > 0 ? (
										<div className="current-plan-history">
											<Stack className="current-plan-history-inner">
												{tabData.map((plan: any, index: number) => {
													const daysDiff = getDaysDifference(plan?.expiryDate);

													return (
														<div
															key={index}
															className={`plan-card ${plan?.isCancelled ? 'cancelled-plan' : ''}`}
														>
															<div className="plan-card-header d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
																<div className="plan-info flex-1 d-flex align-items-center">
																	<div>
																		<h6 className="mb-0">{plan?.planTitle}</h6>
																		{plan?.pricePaid && (
																			<h4 className="fw-bold mb-0">
																				{plan?.currency ? getCurrencySymbol(plan.currency) : ''}
																				{plan.pricePaid}{' '}
																				<span>
																					{translate('Common.forMonths', { month: plan?.duration })}
																				</span>
																			</h4>
																		)}
																	</div>
																	<div className="plan-info-separator"></div>
																	<div className="remaining-days">
																		<p className="mb-0">
																			{daysDiff > 0
																				? translate('Common.remainingDays', { days: daysDiff })
																				: translate('Common.expired')}
																		</p>
																	</div>
																</div>
																<div className="plan-card-header-actions d-flex gap-3">
																	{plan?.invoice_file && (
																		<Button
																			onClick={() => downloadPDF(plan?.invoice_file)}
																			className="transparent-btn rounded-pill lh-1"
																		>
																			<i className="ri-download-line"></i>{' '}
																			{translate('Common.invoice')}
																		</Button>
																	)}
																	{!plan?.isCancelled && plan?.recurringType === 'recurring' && (
																		<Button
																			onClick={() => onCancelSubscription(plan?.id)}
																			className="filled-btn rounded-pill lh-1"
																			disabled={pendingCancelSubscription}
																		>
																			{translate('Extras.cancel')}
																		</Button>
																	)}
																</div>
															</div>
															<div className="plan-card-body d-flex flex-column flex-sm-row gap-3">
																<div className="plan-card-body-content flex-1">
																	<h6 className="fw-bold">{translate('Common.features')}</h6>
																	<Stack className="features-list gap-2">
																		<div className="d-flex align-items-center gap-2">
																			<i className="ri-checkbox-circle-fill lh-1"></i>
																			<p className="mb-0">
																				{translate('Common.numberOfJobPost', {
																					jobNum: plan?.noOfJobs,
																				})}
																			</p>
																		</div>
																		<div className="d-flex align-items-center gap-2">
																			<i className="ri-checkbox-circle-fill lh-1"></i>
																			<p className="mb-0">
																				{translate('Common.interviewSchedules', {
																					number: plan?.noOfMeeting,
																				})}
																			</p>
																		</div>
																	</Stack>
																</div>
																<div className="plan-card-body-content flex-1">
																	<h6 className="fw-bold">{translate('Common.period')}</h6>
																	<p className="mb-0">
																		{translate('Common.planPeriod', {
																			months: plan?.duration,
																		})}
																	</p>
																	{plan?.recurringType && (
																		<p className="mb-0">
																			(
																			{plan?.recurringType === 'recurring'
																				? translate('Extras.recurring')
																				: translate('Extras.one_time')}
																			)
																		</p>
																	)}
																</div>
																<div className="plan-card-body-content flex-1">
																	<h6 className="fw-bold">{translate('PlaceHolder.startDate')}</h6>
																	{plan.purchaseDate && (
																		<p className="mb-0">
																			{moment(plan.purchaseDate).format('DD MMM YYYY')}
																		</p>
																	)}
																</div>
																<div className="plan-card-body-content flex-1">
																	<h6 className="fw-bold">{translate('PlaceHolder.endDate')}</h6>
																	{plan?.expiryDate && (
																		<p className="mb-0">
																			{moment(plan.expiryDate).format('DD MMM YYYY')}
																		</p>
																	)}
																</div>
																{plan?.pricePaid && (
																	<div className="plan-card-body-content flex-1">
																		<h6 className="fw-bold">{translate('Common.totleAmount')}</h6>
																		<p className="mb-0 fw-bold">
																			{plan?.currency ? getCurrencySymbol(plan.currency) : ''}
																			{plan.pricePaid}
																		</p>
																	</div>
																)}
															</div>
														</div>
													);
												})}
												{meta?.totalDocs > meta?.limit && (
													<PaginationBox
														totalData={meta?.totalDocs}
														limit={meta?.limit}
														page={meta?.page}
														setCurrentPage={setCurrentPage}
													/>
												)}
											</Stack>
										</div>
									) : (
										<Col>
											<Stack className="posted-job-box">
												<p className="mb-0">{translate('Common.noDataFound')}</p>
											</Stack>
										</Col>
									)}
								</Row>
							)}
						</Tab>
						<Tab eventKey="history" title={translate('Common.history')}>
							{activeKey === 'history' && isPending ? (
								<JobCardSkeleton />
							) : (
								<Row>
									{tabData?.length > 0 ? (
										<div className="current-plan-history">
											<Stack className="current-plan-history-inner">
												{tabData.map((plan: any, index: number) => {
													const daysDiff = getDaysDifference(plan?.expiryDate);
													return (
														<div key={index} className="plan-card">
															<div className="plan-card-header d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
																<div className="plan-info flex-1 d-flex align-items-center">
																	<div>
																		<h6 className="mb-0">{plan?.planTitle}</h6>
																		{plan?.pricePaid && (
																			<h4 className="fw-bold mb-0">
																				{plan?.currency ? getCurrencySymbol(plan.currency) : ''}
																				{plan.pricePaid}{' '}
																				<span>
																					{translate('Common.forMonths', { month: plan?.duration })}
																				</span>
																			</h4>
																		)}
																	</div>
																	<div className="plan-info-separator"></div>
																	<div className="remaining-days">
																		<p className="mb-0">
																			{daysDiff > 0
																				? translate('Common.remainingDays', { days: daysDiff })
																				: translate('Common.expired')}
																		</p>
																	</div>
																</div>
																<div className="plan-card-header-actions d-flex gap-3">
																	{plan?.invoice_file && (
																		<Button
																			onClick={() => downloadPDF(plan?.invoice_file)}
																			className="transparent-btn rounded-pill lh-1"
																		>
																			<i className="ri-download-line"></i>{' '}
																			{translate('Common.invoice')}
																		</Button>
																	)}
																	{authUser?.userType === USER_TYPES.COMPANY && (
																		<Button
																			onClick={() => navigate('/company/subscriptions')}
																			className="filled-btn rounded-pill lh-1"
																		>
																			{translate('Common.renew')}
																		</Button>
																	)}
																</div>
															</div>
															<div className="plan-card-body d-flex flex-column flex-sm-row gap-3">
																<div className="plan-card-body-content flex-1">
																	<h6 className="fw-bold">{translate('Common.features')}</h6>
																	<Stack className="features-list gap-2">
																		<div className="d-flex align-items-center gap-2">
																			<i className="ri-checkbox-circle-fill lh-1"></i>
																			<p className="mb-0">
																				{translate('Common.numberOfJobPost', {
																					jobNum: plan?.noOfJobs,
																				})}
																			</p>
																		</div>
																		<div className="d-flex align-items-center gap-2">
																			<i className="ri-checkbox-circle-fill lh-1"></i>
																			<p className="mb-0">
																				{translate('Common.interviewSchedules', {
																					number: plan?.noOfMeeting,
																				})}
																			</p>
																		</div>
																	</Stack>
																</div>
																<div className="plan-card-body-content flex-1">
																	<h6 className="fw-bold">{translate('Common.period')}</h6>
																	<p className="mb-0">
																		{translate('Common.planPeriod', {
																			months: plan?.duration,
																		})}
																	</p>
																	{plan?.recurringType && (
																		<p className="mb-0">
																			(
																			{plan?.recurringType === 'recurring'
																				? translate('Extras.recurring')
																				: translate('Extras.one_time')}
																			)
																		</p>
																	)}
																</div>
																<div className="plan-card-body-content flex-1">
																	<h6 className="fw-bold">{translate('PlaceHolder.startDate')}</h6>
																	{plan.purchaseDate && (
																		<p className="mb-0">
																			{moment(plan.purchaseDate).format('DD MMM YYYY')}
																		</p>
																	)}
																</div>
																<div className="plan-card-body-content flex-1">
																	<h6 className="fw-bold">{translate('PlaceHolder.endDate')}</h6>
																	{plan?.expiryDate && (
																		<p className="mb-0">
																			{moment(plan.expiryDate).format('DD MMM YYYY')}
																		</p>
																	)}
																</div>
																{plan?.pricePaid && (
																	<div className="plan-card-body-content flex-1">
																		<h6 className="fw-bold">{translate('Common.totleAmount')}</h6>
																		<p className="mb-0 fw-bold">
																			{plan?.currency ? getCurrencySymbol(plan.currency) : ''}
																			{plan.pricePaid}
																		</p>
																	</div>
																)}
															</div>
														</div>
													);
												})}
												{meta?.totalDocs > meta?.limit && (
													<PaginationBox
														totalData={meta?.totalDocs}
														limit={meta?.limit}
														page={meta?.page}
														setCurrentPage={setCurrentPage}
													/>
												)}
											</Stack>
										</div>
									) : (
										<Col>
											<Stack className="posted-job-box">
												<p className="mb-0">{translate('Common.noDataFound')}</p>
											</Stack>
										</Col>
									)}
								</Row>
							)}
						</Tab>
					</Tabs>
				</div>
			</Stack>
		</div>
	);
};

export default CurrentPlanHistory;
